import type { ThemeOptions } from '@mui/material/styles';

import {
  experimental_extendTheme as extendTheme,
  createTheme,
} from '@mui/material/styles';
import merge from 'lodash/merge';

import { BREAKPOINT_VALUES } from './breakpoints';
import { createContrast } from './options/contrast';
import { componentsOverrides } from './overrides';
import { customShadows } from './custom-shadows';
import { typography } from './typography';
import { palette } from './palette';
import { shadows } from './shadows';

/**
 * ONE set of numbers for the two styling systems.
 *
 * Tailwind and MUI both do responsive in this app, and their scales disagreed:
 * `md` meant 768px in a className and 900px in an `sx`, `lg` 1024 vs 1200. A
 * layout written against one broke inside the band the other owned, and nine
 * files use both. Read straight off the Tailwind config rather than copied, so
 * the two cannot drift again; `theme/__tests__` pins the parity.
 *
 * MUI is the side that moved: the layout is mostly written in Tailwind classes,
 * so aligning the other way would have shifted the larger half of the app.
 * `xs` has no Tailwind name — it is the base stop, below every prefix.
 */
declare module '@mui/material/styles' {
  // Tailwind's sixth stop. Declared so `sx={{ p: { '2xl': 4 } }}` and
  // `theme.breakpoints.up('2xl')` mean the same 1536px the class does; MUI
  // derives its key order from the values it is handed, so the runtime side
  // needs nothing beyond `BREAKPOINT_VALUES`.
  interface BreakpointOverrides {
    '2xl': true;
  }
}

export interface AppThemeSettings {
  themeContrast: 'bold' | 'default';
  themeDirection: 'ltr' | 'rtl';
  /**
   * The resolved `--mfw-palette-*` record for the tenant/org being rendered.
   * Optional only so a caller with nothing resolved yet still gets the catalog
   * defaults — every real caller passes it, and passing it is what keeps the JS
   * palette and the CSS vars from being two different answers.
   */
  vars?: Record<string, string>;
}

/**
 * Builds the two theme objects the app runs on, in the one order that works.
 *
 * `ThemeProvider` nests `CssVarsProvider` INSIDE `MuiThemeProvider`, so the
 * css-vars theme is the one components actually resolve against — the plain
 * theme only serves anything rendered outside that inner provider (today:
 * nothing). Whatever the css-vars theme does not carry therefore does not
 * exist, which is how `theme.components` — assigned by mutation AFTER the
 * snapshot was taken — left every override in `theme/overrides/` inert for as
 * long as it did. So: build the components INTO the theme, then extend it.
 *
 * Pure, and a function rather than three statements inside a component, so the
 * seam can be asserted (`__tests__/createAppTheme.spec.ts`) instead of only
 * being visible by measuring a rendered avatar.
 *
 * NOTE on the palette: `extendTheme` reads a bare `palette` key as the LIGHT
 * color scheme's palette, and the provider paints the light scheme onto
 * `:root`. That is why the app's dark surfaces reach the DOM even though they
 * are declared as `mode: 'dark'` — the naming is MUI's, the values are ours.
 * `AppThemeProvider` then overwrites the `--mfw-palette-*` vars per tenant.
 */
export const createAppTheme = ({
  themeContrast,
  themeDirection,
  vars,
}: AppThemeSettings) => {
  const theme = createTheme({
    breakpoints: { values: BREAKPOINT_VALUES },
    customShadows: { ...customShadows() },
    direction: themeDirection,
    palette: palette(vars),
    shadows: shadows(),
    shape: { borderRadius: 8 },
    typography,
  } as ThemeOptions);

  theme.components = merge(
    componentsOverrides(theme),
    createContrast(themeContrast).components,
  );

  return {
    cssVarsTheme: extendTheme({ ...theme, cssVarPrefix: 'mfw' }),
    theme,
  };
};
